import React from 'react';
import {
  array, arrayOf, bool, number, object, objectOf, shape, string
} from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { copy } from '@thd-olt-functional/utils';

function getDimension(suppDimensions, entryGroup, dimension) {
  const entry = suppDimensions.filter(({ specTitle }) => specTitle === entryGroup);
  let spec;
  if (entry.length) {
    spec = entry[0].specifications
      .sort((specA, specB) => {
        if (specA.specName < specB.specName) {
          return -1;
        }
        if (specA.specName > specB.specName) {
          return 1;
        }
        return 0;
      })
      .find(({ specName = '' }) => specName.toLowerCase().indexOf(dimension) > -1) || {};
  }
  return spec?.specValue || '';
}

const ProductStructuredData = ({
  aggregateReviews,
  availabilityType,
  identifiers,
  media,
  details,
  reviews,
  pricing,
  specifications,
  schemaExclude
}) => {
  const {
    itemId,
    productLabel = '',
    brandName = '',
    upcGtin13 = '',
    storeSkuNumber = '',
    canonicalUrl = ''
  } = identifiers || {};
  const { value, promotion } = pricing || {};
  const { description } = details || {};
  const today = new Date(Date.now());

  const specs = copy(specifications || []).data;

  const schemaString = JSON.stringify({
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: (schemaExclude?.includes('name')) ? undefined : productLabel,
    image: (schemaExclude?.includes('image'))
      ? undefined : media?.images?.map(({ url }) => url.replace('<SIZE>', '100')),
    description: (schemaExclude?.includes('description')) ? undefined : description,
    productID: (schemaExclude?.includes('productID')) ? undefined : itemId,
    sku: (schemaExclude?.includes('sku')) ? undefined : storeSkuNumber,
    gtin13: (schemaExclude?.includes('gtin13')) ? undefined : upcGtin13,
    depth: (schemaExclude?.includes('depth')) ? undefined : getDimension(specs, 'Dimensions', 'depth'),
    height: (schemaExclude?.includes('height')) ? undefined : getDimension(specs, 'Dimensions', 'height'),
    width: (schemaExclude?.includes('width')) ? undefined : getDimension(specs, 'Dimensions', 'width'),
    color: (schemaExclude?.includes('color')) ? undefined : getDimension(specs, 'Details', 'color'),
    weight: (schemaExclude?.includes('width')) ? undefined : getDimension(specs, 'Details', 'weight'),
    brand: (schemaExclude?.includes('brand')) ? undefined : {
      '@type': 'Thing',
      name: brandName,
    },
    ...(Number(aggregateReviews?.totalReviews) && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: aggregateReviews?.averageRating,
        reviewCount: aggregateReviews?.totalReviews,
      },
    }),
    offers: (schemaExclude?.includes('offers')) ? undefined : {
      '@type': 'Offer',
      url: `https://www.homedepot.com${canonicalUrl}`,
      priceCurrency: 'USD',
      price: value,
      priceValidUntil:
        (promotion && promotion?.dates?.end) || `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`,
      availability: availabilityType?.status ? 'https://schema.org/InStock' : undefined,
    },
    review: (schemaExclude?.includes('review')) ? undefined : (reviews || []).map((review) => ({
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.Rating,
        bestRating: '5',
      },
      author: {
        '@type': 'Person',
        name: review.UserNickname,
      },
      headline: review.Title,
      reviewBody: review.ReviewText,
    })),
  });
  return (
    <Helmet>
      <script type="application/ld+json">{schemaString}</script>
    </Helmet>
  );
};

ProductStructuredData.propTypes = {
  reviews: arrayOf(shape({})),
  availabilityType: shape({
    discontinued: bool,
    status: bool
  }),
  aggregateReviews: objectOf(string),
  identifiers: shape({
    itemId: string,
    productLabel: string,
    brandName: string,
    upcGtin13: string,
    storeSkuNumber: string,
    canonicalUrl: string,
  }),
  media: shape({
    // eslint-disable-next-line
    images: arrayOf(object)
  }),
  details: shape({
    description: string,
    // eslint-disable-next-line
    descriptiveAttributes: array
  }),
  pricing: shape({
    value: number,
    promotion: shape({
      isCartLevelPromotion: bool,
      dates: shape({
        end: string
      })
    })
  }),
  specifications: arrayOf(shape({})),
  schemaExclude: arrayOf(string)
};

ProductStructuredData.defaultProps = {
  availabilityType: {},
  reviews: [],
  aggregateReviews: {},
  identifiers: {},
  media: {},
  details: {},
  pricing: {},
  specifications: [],
  schemaExclude: [],
};

export { ProductStructuredData };
